import { type MaybeElementRef } from "@vueuse/core";

interface DragState {
  from: number | null;
  to: number | null;
}

export function useDrag(container: MaybeElementRef<HTMLElement | undefined>) {
  const state = reactive<DragState>({
    from: null,
    to: null,
  });

  const draggedElementWidth = ref(0);
  const initialMousePosition = ref(0);
  const mouseDelta = ref(0);
  const elementsMoved = computed(() =>
    Math.round(mouseDelta.value / draggedElementWidth.value)
  );

  const dragging = ref(false);

  const resetState = () => {
    state.from = null;
    state.to = null;
    draggedElementWidth.value = 0;
    mouseDelta.value = 0;
    initialMousePosition.value = 0;
    dragging.value = false;
  };

  const adjustedMousePosition = (e: DragEvent) => {
    const unrefed = unrefElement(container);
    const scrollOffset = unrefed?.scrollLeft ?? 0;
    return e.pageX + scrollOffset;
  };

  const { x } = useMouse();

  const dragScrollInterval = ref();

  function easeInOutQuad(x: number): number {
    return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
  }

  const listenForDragScroll = (el: HTMLElement) => {
    const leftDelta = x.value - el.offsetLeft;
    const rightDelta = el.offsetWidth - (x.value - el.offsetLeft);
    if (leftDelta < 50) {
      el.scrollLeft -= easeInOutQuad((leftDelta - 50) / 50) * 5;
    } else if (rightDelta < 50) {
      el.scrollLeft += easeInOutQuad((rightDelta - 50) / 50) * 5;
    }
  };

  watch(dragging, (value) => {
    if (value) {
      const el = unrefElement(container);
      if (el) {
        dragScrollInterval.value = setInterval(
          () => listenForDragScroll(el),
          50
        );
      }
    } else {
      clearInterval(dragScrollInterval.value);
    }
  });

  onUnmounted(() => clearInterval(dragScrollInterval.value));

  const dragStart = (e: DragEvent, position: number, element: HTMLElement) => {
    if (e.dataTransfer === null) {
      return;
    }
    e.dataTransfer.dropEffect = "move";
    e.dataTransfer.effectAllowed = "move";
    state.from = position;
    const rect = element.getBoundingClientRect();
    draggedElementWidth.value = rect.width;
    initialMousePosition.value = adjustedMousePosition(e);
    dragging.value = true;
  };

  useEventListener("dragend", () => resetState());

  const dragOver = (e: DragEvent) => {
    e.preventDefault();
    mouseDelta.value = adjustedMousePosition(e) - initialMousePosition.value;
  };

  const setDropIndex = (e: DragEvent) => {
    e.preventDefault();
    mouseDelta.value = adjustedMousePosition(e) - initialMousePosition.value;
    if (state.from === null) {
      throw Error("No start for drag action is set.");
    }
    state.to = state.from + elementsMoved.value;
  };

  return {
    dragStart,
    dragOver,
    setDropIndex,
    state: readonly(state),
    elementsMoved: readonly(elementsMoved),
    dragging: readonly(dragging),
  };
}
